<script lang="ts">
  import { onMount } from "svelte";
  import { push, link } from "svelte-spa-router";
  import {
    listAuthorities,
    listSchools,
    createSchool,
    deleteSchool,
    updateAuthority,
    ApiError,
  } from "../lib/api";
  import Breadcrumb from "../lib/Breadcrumb.svelte";
  import { scopes } from "../lib/session.svelte";
  import type { Authority, School } from "@diy-avatar/shared/admin";

  export let params: { id?: string } = {};
  $: authorityId = params.id ? parseInt(params.id, 10) : NaN;

  let authority: Authority | null = null;
  let schools: School[] = [];
  let loading = false;
  let error: string | null = null;

  let newName = "";
  let newDescription = "";

  $: canManage = $scopes.isAdmin;

  function handleAuth(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 401) {
      push("/");
      return true;
    }
    return false;
  }

  async function load(id: number) {
    if (!Number.isFinite(id)) return;
    loading = true;
    try {
      const [auths, sc] = await Promise.all([listAuthorities(), listSchools()]);
      authority = auths.authorities.find((a) => a.id === id) ?? null;
      schools = sc.schools.filter((s) => s.authority_id === id);
      error = null;
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "Could not load authority.";
    } finally {
      loading = false;
    }
  }

  async function toggleForbidInvisible(checked: boolean) {
    if (!authority) return;
    error = null;
    try {
      await updateAuthority(authority.id, { forbid_invisible: checked });
      await load(authorityId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "save failed";
    }
  }

  async function toggleForceE2eeQr(checked: boolean) {
    if (!authority) return;
    error = null;
    try {
      await updateAuthority(authority.id, { force_e2ee_qr: checked });
      await load(authorityId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "save failed";
    }
  }

  async function toggleForceE2ee(checked: boolean) {
    if (!authority) return;
    error = null;
    try {
      await updateAuthority(authority.id, { force_e2ee: checked });
      await load(authorityId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "save failed";
    }
  }

  async function setRotateHour(raw: string) {
    if (!authority) return;
    error = null;
    const hour = raw === "" ? null : parseInt(raw, 10);
    if (hour !== null && (!Number.isInteger(hour) || hour < 0 || hour > 23)) {
      error = "rotation hour must be 0-23";
      return;
    }
    try {
      await updateAuthority(authority.id, { e2ee_rotate_hour: hour });
      await load(authorityId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "save failed";
    }
  }

  async function doCreate() {
    error = null;
    if (!newName.trim()) {
      error = "name required";
      return;
    }
    try {
      await createSchool({
        name: newName.trim(),
        authority_id: authorityId,
        description: newDescription.trim() || null,
      });
      newName = "";
      newDescription = "";
      await load(authorityId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "create failed";
    }
  }

  async function doDelete(s: School) {
    if (!confirm(`Delete "${s.name}"? This cascades to rooms/devices.`)) return;
    try {
      await deleteSchool(s.id);
      await load(authorityId);
    } catch (err) {
      if (handleAuth(err)) return;
      error = err instanceof Error ? err.message : "delete failed";
    }
  }

  $: if (Number.isFinite(authorityId)) void load(authorityId);
  onMount(() => {});
</script>

<section>
  <Breadcrumb
    items={[
      { label: "Authorities", href: "/authorities" },
      { label: authority?.name ?? "…" },
    ]}
  />
  <h1>{authority?.name ?? "Authority"}</h1>
  {#if error}<p class="error" data-testid="error">{error}</p>{/if}

  {#if authority}
    <div class="panel" data-testid="authority-settings">
      <h2>Settings</h2>
      <label class="toggle">
        <input
          type="checkbox"
          checked={authority.forbid_invisible}
          disabled={!canManage}
          on:change={(e) => toggleForbidInvisible(e.currentTarget.checked)}
          data-testid="forbid-invisible"
        />
        Forbid invisible mode
      </label>
      <p class="hint">
        When enabled, students under this authority may not use invisible mode.
      </p>
      <label class="toggle">
        <input
          type="checkbox"
          checked={authority.force_e2ee_qr}
          disabled={!canManage}
          on:change={(e) => toggleForceE2eeQr(e.currentTarget.checked)}
          data-testid="force-e2ee-qr"
        />
        QR-Schlüsselaustausch erzwingen
      </label>
      <p class="hint e2ee-qr-desc">
        <strong>QR-Schlüsselaustausch erzwingen.</strong> Wenn aktiv, müssen
        Schüler:innen den QR-Code am Klassen-Display <strong>persönlich
        scannen</strong>. Der Schlüssel verlässt dabei nie unsere Server —
        höchste Sicherheit, aber alle müssen physisch anwesend sein. Wenn
        deaktiviert, werden Schlüssel automatisch verteilt (für jedes
        Schüler-Gerät versiegelt), sodass auch entfernte Schüler:innen ohne
        Scannen verschlüsselt teilnehmen — das vertraut darauf, dass der Server
        korrekte öffentliche Schlüssel ausliefert. Aktivierung auf
        <strong>einer beliebigen Ebene</strong> (Schulamt/Schule/Raum/Gerät)
        erzwingt QR für <strong>alle</strong> darunterliegenden Geräte und
        Schüler:innen.
      </p>
      <label class="toggle">
        <input
          type="checkbox"
          checked={authority.force_e2ee}
          disabled={!canManage}
          on:change={(e) => toggleForceE2ee(e.currentTarget.checked)}
          data-testid="force-e2ee"
        />
        Verschlüsselung erzwingen
      </label>
      <p class="hint e2ee-desc">
        <strong>Verschlüsselung erzwingen.</strong> Wenn aktiv, ist
        Ende-zu-Ende-Verschlüsselung für alle Geräte und Schüler:innen darunter
        verpflichtend (kein Opt-out, nicht unterstützte Browser werden
        blockiert). Schlüssel werden weiterhin <strong>automatisch verteilt</strong>
        (für jedes Schüler-Gerät versiegelt) — anders als „QR erzwingen“, das die
        automatische Verteilung abschaltet. Aktivierung auf
        <strong>einer beliebigen Ebene</strong> (Schulamt/Schule/Raum/Gerät)
        erzwingt Verschlüsselung für <strong>alle</strong> darunterliegenden
        Geräte und Schüler:innen.
      </p>
      <label class="field">
        <span>Tägliche E2EE-Schlüsselrotation (Stunde, Ortszeit)</span>
        <select
          value={authority.e2ee_rotate_hour === null
            ? ""
            : String(authority.e2ee_rotate_hour)}
          disabled={!canManage}
          on:change={(e) => setRotateHour(e.currentTarget.value)}
          data-testid="e2ee-rotate-hour"
        >
          <option value="">Vererbt (Standard)</option>
          {#each Array(24) as _, h}
            <option value={String(h)}>{String(h).padStart(2, "0")}:00</option>
          {/each}
        </select>
      </label>
      <p class="hint">
        Stunde (0-23, Ortszeit), zu der der E2EE-Medienschlüssel täglich rotiert
        wird. Leer = von der übergeordneten Ebene erben (Standard 00:00).
      </p>
    </div>
  {/if}

  <h2>Schools</h2>
  <p class="hint">
    Manage each school's rooms, teachers, students and devices from its page.
    School-managers are granted per school (each school keeps ≥ 1 manager).
  </p>

  <div class="new" data-testid="new-school">
    <div class="row">
      <input
        type="text"
        placeholder="School name"
        bind:value={newName}
        data-testid="new-school-name"
      />
      <input
        type="text"
        placeholder="Description (optional)"
        bind:value={newDescription}
        data-testid="new-school-description"
      />
      <button on:click={doCreate} data-testid="new-school-submit">Create school</button>
    </div>
  </div>

  <table data-testid="schools-table">
    <thead>
      <tr><th>id</th><th>name</th><th>description</th><th>created</th><th>actions</th></tr>
    </thead>
    <tbody>
      {#each schools as s (s.id)}
        <tr data-testid="school-row-{s.id}">
          <td>#{s.id}</td>
          <td>
            <a href="/schools/{s.id}" use:link data-testid="school-link-{s.id}">{s.name}</a>
          </td>
          <td>{s.description ?? "—"}</td>
          <td>{new Date(s.created_at).toISOString().slice(0, 10)}</td>
          <td class="actions">
            <button on:click={() => doDelete(s)}>delete</button>
          </td>
        </tr>
      {/each}
      {#if schools.length === 0 && !loading}
        <tr><td colspan="5" class="empty">No schools yet.</td></tr>
      {/if}
    </tbody>
  </table>
</section>

<style>
  h1 { margin: 0 0 0.5rem; }
  h2 { margin: 1rem 0 0.4rem; font-size: 1.05rem; color: #34d399; }
  .hint { color: #9ca3af; font-size: 0.85rem; margin: 0 0 0.75rem; }
  .e2ee-qr-desc {
    color: #d1d5db;
    background: #1c1407;
    border-left: 3px solid #b45309;
    padding: 0.6rem 0.8rem;
    border-radius: 4px;
    line-height: 1.4;
  }
  .e2ee-qr-desc strong { color: #fbbf24; }
  .new {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  .panel {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    background: #111827;
    border: 1px solid #1f2937;
    border-radius: 8px;
  }
  .toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: #d1d5db;
    font-size: 0.85rem;
  }
  .toggle input { padding: 0; }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    color: #d1d5db;
    font-size: 0.85rem;
    margin: 0.5rem 0 0;
  }
  .field select {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
    max-width: 16rem;
  }
  .row { display: flex; gap: 0.5rem; flex-wrap: wrap; }
  input {
    padding: 0.4rem 0.5rem;
    background: #0f172a;
    color: #f3f4f6;
    border: 1px solid #1f2937;
    border-radius: 4px;
  }
  button {
    padding: 0.4rem 0.7rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 4px;
  }
  table { width: 100%; border-collapse: collapse; background: #111827; }
  th, td {
    padding: 0.4rem 0.6rem;
    text-align: left;
    border-bottom: 1px solid #1f2937;
    font-size: 0.85rem;
  }
  th { background: #0f172a; color: #9ca3af; }
  td a { color: #34d399; text-decoration: none; }
  td a:hover { text-decoration: underline; }
  .actions { display: flex; gap: 0.3rem; }
  .empty { color: #6b7280; text-align: center; }
  .error { color: #fca5a5; }
</style>
